/*
 *
 *  * (C) Copyright 2006-2020 hSenid Mobile Solutions (Pvt) Limited.
 *  *
 *  * All Rights Reserved.
 *  *
 *  * These materials are unpublished, proprietary, confidential source code of
 *  * hSenid Mobile Solutions (Pvt) Limited and constitute a TRADE SECRET
 *  * of hSenid Mobile Solutions (Pvt) Limited.
 *  *
 *  * hSenid Mobile Solutions (Pvt) Limited retains all title to and intellectual
 *  * property rights in these materials.
 *  *
 *
 */

import React from "react";
import { FacebookShareButton, TwitterShareButton } from "react-share";
import { useDispatch } from "react-redux";
import { AppDetailsPageEvent, useAppDetailsPageDataSelector } from ".";
import { MainPageEvent, useMainPageDataSelector } from "../../MainUi";
import { Img } from "../../Common/element/Img";
import { App } from "../../../api/apiCalls";
import RateAppComponent from "../Reviews/RateAppComponent";

interface AppDetailsProps {
  app: App;
}

export default function AppDetailsView({ app }: AppDetailsProps) {
  const mainPageData = useMainPageDataSelector();
  const pageData = useAppDetailsPageDataSelector();
  const dispatch = useDispatch();

  const isDownloadableProduction = app?.ncses?.some(
    (ncse) =>
      ncse.ncsType === "downloadable" && ncse.status === "active-production"
  );

  const isSubscriptionProduction = app?.ncses?.some(
    (ncse) =>
      ncse.ncsType === "subscription" && ncse.status === "active-production"
  );

  const btnClick = () => {
    if (isDownloadableProduction) {
      downloadBuildfileAction();
    } else if (isSubscriptionProduction) {
      subscriptionModalAction();
    }
  };

  const downloadBuildfileAction = () => {
    if (mainPageData.authState) {
      dispatch({
        type: AppDetailsPageEvent.DOWNLOAD_BUILDFILE,
        payload: {
          fileName: app?.buildfile?.appFilePath,
          spId: app?.buildfile?.spId,
          appId: app?.appId,
        },
      });
    } else {
      dispatch({
        type: MainPageEvent.SHOW_SIGNIN_MODAL,
      });
    }
  };

  const subscriptionModalAction = () => {
    if (mainPageData.authState) {
      if (pageData.subscriptionStatus) {
        dispatch({
          type: MainPageEvent.SHOW_UNSUBSCRIPTION_MODAL,
          payload: app,
        });
      } else {
        dispatch({
          type: MainPageEvent.SHOW_SUBSCRIPTION_MODAL,
          payload: app,
        });
      }
    } else {
      dispatch({
        type: MainPageEvent.SHOW_SIGNIN_MODAL,
      });
    }
  };

  const isSubscriptionNcs = app?.ncses?.find(
    (ncs) => ncs.ncsType === "subscription"
  );

  return (
    <div className="row">  
      <div className="col-sm-4">
        <div className="app-details-image">
          {isSubscriptionNcs?.free && (
            <div className="app-details-image__app-type">Free</div>
          )}
          <div className="app-details-image__share">
            <FacebookShareButton
              quote={"To use " + app?.name + " visit below link."}
              hashtag={"#bdapps"}
              url={document.URL}
            >
              <div className="btn btn-light text-secondary mr-1">
                <i className="fab fa-facebook"></i>
              </div>
            </FacebookShareButton>
            <TwitterShareButton
              title={"To use " + app?.name + " visit below link."}
              hashtags={["bdapps"]}
              url={document.URL}
            >
              <div className="btn btn-light text-secondary">
                <i className="fab fa-twitter"></i>
              </div>
            </TwitterShareButton>
          </div>
          {app?.appLogo ? (
            <Img
              src={app.appLogo}
              className="carousel-item__image d-block w-100"
            />
          ) : (
            <></>
          )}
        </div>
        <RateAppComponent />
      </div>
      <div className="col-sm-8">
        <div className="row">
          <div className="col-lg-8">
            <h4 className="font-weight-bold mt-sm-0 mt-3 mb-0">{app?.name}</h4>
            <p className="sub-text">{`By ${app?.developerName}`}</p>
            <p className="text-primary">
              <i className="fas fa-star" />
              &nbsp;{app && Math.round(app.rating * 10) / 10}
            </p>
          </div>
          <div className="col-lg-4 text-lg-right">
            {isDownloadableProduction ? (
              <button
                disabled={pageData.downloading}
                className="btn btn-primary"
                type="button"
                onClick={() => btnClick()}
              >
                <i
                  className={
                    pageData.downloading ? "fas fa-spin fa-circle-notch" : ""
                  }
                ></i>
                {pageData.downloading ? " Downloading..." : "Download"}
              </button>
            ) : isSubscriptionProduction ? (
              <button
                key={app?.id}
                className="btn btn-primary"
                onClick={() => btnClick()}
              >
                <i className="fas fa-rss"></i>{" "}
                {pageData.subscriptionStatus ? "Unsubscribe" : "Subscribe"}
              </button>
            ) : (
              <></>
            )}
          </div>
        </div>
        <p className="font-weight-bold mt-2 mb-0">Description</p>
        <p className="sub-text">
          {app?.detailedDescription
            ? app.detailedDescription
            : "No information to be shown"}
        </p>
        <p className="font-weight-bold m-0">Cost</p>
        {app?.ncses?.map((ncs) => {
          if (ncs.ncsType === "subscription") {
            return (
              <p key={ncs.ncsType} className="sub-text">
                {`Subscription: ${
                  ncs.free
                    ? "Free"
                    : `${ncs.frequency} ${ncs.amount} ${ncs.currencyCode} + VAT and SD.`
                }`}
              </p>
            );
          } else if (ncs.ncsType === "sms") {
            return (
              <p key={ncs.ncsType} className="sub-text">
                {`Receiving messages from this application: ${
                  ncs.mo?.free
                    ? "Free"
                    : `${ncs.mo?.currencyCode} ${ncs.mo?.amount} + VAT and SD.`
                }`}
                <br />
                {`Sending messages to this application: ${
                  ncs.mt?.free
                    ? "Free"
                    : `${ncs.mt?.currencyCode} ${ncs.mt?.amount} + VAT and SD.`
                }`}
              </p>
            );
          } else if (ncs.ncsType === "ussd") {
            return (
              <p key={ncs.ncsType} className="sub-text">
                {`Receiving USSD messages from this application: ${
                  ncs.mo?.free
                    ? "Free"
                    : `${ncs.mo?.currencyCode} ${ncs.mo?.amount} + VAT and SD.`
                }`}
                <br />
                {`Sending USSD messages to this application: ${
                  ncs.mt?.free
                    ? "Free"
                    : `${ncs.mt?.currencyCode} ${ncs.mt?.amount} + VAT and SD.`
                }`}
              </p>
            );
          } else {
            return (
              <p key={ncs.ncsType} className="sub-text">
                {`Separate charges may apply for services provided by this application.`}
              </p>
            );
          }
        })}
        <p className="font-weight-bold m-0">Instruction</p>
        <p className="sub-text">
          {app?.instruction ? app.instruction : "No information to be shown"}
        </p>
      </div>
    </div>
  );
}
